import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GradButton } from "@/components/erp";
import api, { errMsg } from "@/lib/api";

const DURUM = { yeni: ["Yeni bağlantı", "text-emerald-300"], guncelle: ["Güncellenecek", "text-sky-300"], atla: ["Atlanacak", "text-muted-foreground"], hata: ["Hatalı", "text-rose-300"] };

const Ozet = ({ o }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="ka-ozet">
    {[["Yeni bağlantı", o.yeni, "ka-yeni"], ["Güncellenecek", o.guncelle, "ka-guncelle"], ["Hatalı", o.hata, "ka-hata"], ["Atlanacak", o.atla, "ka-atla"],
      ["Kalıp serisi", o.seri, "ka-seri"], ["Açılacak kalıp kartı", o.yeni_kalip, "ka-yeni-kalip"], ["Stok kodu boş satır", o.kodsuz, "ka-kodsuz"]].map(([l, v, t]) => (
      <div key={t} className="rounded-xl bg-white/5 p-3"><div className="text-[11px] text-muted-foreground">{l}</div><div className="font-semibold font-mono" data-testid={t}>{v ?? 0}</div></div>))}
  </div>
);

export const KalipAktarim = ({ onDone }) => {
  const [acik, setAcik] = useState(false);
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const indir = async () => {
    try {
      const { data } = await api.get("/kalip-aktarim/sablon", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "kalip-aktarim-sablonu.xlsx"; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const yukle = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("dosya", f);
    try { setOn((await api.post("/kalip-aktarim/onizle", fd)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const uygula = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/kalip-aktarim/uygula", { token: on.token });
      toast.success(`${data.urun} ürün bağlandı, ${data.yeni_kalip} kalıp kartı açıldı`); setOn(null); setAcik(false); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="kalip-aktar" onClick={() => setAcik(true)} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm border border-white/15 hover:bg-white/5"><FileSpreadsheet className="w-4 h-4" />Excel'den toplu aktar</button>
      {acik && <Dialog open onOpenChange={(o) => !o && !busy && (setAcik(false), setOn(null))}>
        <DialogContent className="glass max-w-4xl max-h-[90vh] overflow-y-auto erp-scroll" data-testid="kalip-aktar-dialog">
          <DialogHeader><DialogTitle className="font-head">Toplu kalıp aktarımı</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Aynı "Kalıp Serisi"ni yazan ürünler ortak kalıp havuzunu paylaşır; havuzda serideki en büyük adet kadar kalıp kartı açılır. Mevcut kalıplar silinmez. WooCommerce ürün dışa aktarım dosyası da doğrudan yüklenebilir.</p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="ka-sablon" onClick={indir} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm border border-white/15 hover:bg-white/5"><Download className="w-4 h-4" />Örnek şablonu indir</button>
            <GradButton data-testid="ka-dosya-sec" disabled={busy} onClick={() => ref.current.click()}><Upload className="w-4 h-4" />{busy && !on ? "Okunuyor…" : "Dosya seç ve önizle"}</GradButton>
            <input ref={ref} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="ka-dosya" onChange={(e) => yukle(e.target.files?.[0])} />
          </div>
          {on && <>
            <Ozet o={on.ozet} />
            <div className="overflow-x-auto max-h-[40vh] erp-scroll rounded-xl border border-white/10"><table className="w-full text-xs" data-testid="ka-tablo">
              <thead className="sticky top-0 bg-[var(--card,#111)]"><tr className="text-left text-muted-foreground"><th className="p-2">Satır</th><th className="p-2">Stok kodu</th><th className="p-2">Seri</th><th className="p-2 text-right">Kalıp</th><th className="p-2 text-right">Bakımda</th><th className="p-2 text-right">Döngü</th><th className="p-2">Kazan</th><th className="p-2">Durum</th></tr></thead>
              <tbody>{on.satirlar.map((r) => (
                <tr key={r.satir} className="border-t border-white/5" data-testid={`ka-satir-${r.satir}`}>
                  <td className="p-2 font-mono">{r.satir}</td><td className="p-2"><div className="font-mono">{r.kod}</div><div className="text-muted-foreground truncate max-w-[220px]">{r.urun_ad}</div></td>
                  <td className="p-2">{r.seri || "—"}</td><td className="p-2 text-right font-mono">{r.adet ?? "—"}</td><td className="p-2 text-right font-mono">{r.bakim ?? "—"}</td>
                  <td className="p-2 text-right font-mono">{r.dongu ?? "—"}</td><td className="p-2">{r.kazan || "—"}</td>
                  <td className={`p-2 ${DURUM[r.durum][1]}`}>{DURUM[r.durum][0]}{r.mesaj ? ` · ${r.mesaj}` : ""}</td>
                </tr>))}</tbody>
            </table></div>
            {on.satirlar.length >= 1000 && <p className="text-[11px] text-muted-foreground">İlk 1000 satır gösteriliyor; tümü uygulanır.</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setOn(null)} className="px-5 py-2 rounded-full border border-white/10 text-sm" data-testid="ka-vazgec">Vazgeç</button>
              <GradButton data-testid="ka-onayla" disabled={busy || !(on.ozet.yeni + on.ozet.guncelle)} onClick={uygula}><Check className="w-4 h-4" />{busy ? "Aktarılıyor…" : `${on.ozet.yeni + on.ozet.guncelle} ürünü aktar`}</GradButton>
            </div>
          </>}
        </DialogContent>
      </Dialog>}
    </>
  );
};
